import { useState, useEffect } from 'react';
import { TOKEN_LOGO_SOURCES, DEFAULT_TOKEN_LOGOS } from '../config/staking';

// Cache for token logos
const logoCache = new Map();

/**
 * Auto-detect token logo from multiple sources
 * Priority: Default > TrustWallet > PancakeSwap > Fallback
 */
export const getTokenLogo = async (tokenAddress, chainId = 56) => {
  if (!tokenAddress) return null;

  const address = tokenAddress.toLowerCase();
  
  // Check cache first
  if (logoCache.has(address)) {
    return logoCache.get(address);
  }

  // Check default logos
  if (DEFAULT_TOKEN_LOGOS[address]) {
    logoCache.set(address, DEFAULT_TOKEN_LOGOS[address]);
    return DEFAULT_TOKEN_LOGOS[address];
  }

  // Try external sources
  for (const source of TOKEN_LOGO_SOURCES) {
    try {
      const url = source.getUrl(chainId, tokenAddress);
      const response = await fetch(url, { method: 'HEAD' });
      
      if (response.ok) {
        logoCache.set(address, url);
        return url;
      }
    } catch (error) {
      // Continue to next source
      continue;
    }
  }

  // Fallback: generate placeholder based on symbol
  return null;
};

/**
 * Generate placeholder logo based on token symbol
 */
export const generatePlaceholderLogo = (symbol) => {
  if (!symbol) return null;
  
  const firstChar = symbol.charAt(0).toUpperCase();
  const canvas = document.createElement('canvas');
  canvas.width = 96;
  canvas.height = 96;
  const ctx = canvas.getContext('2d');
  
  // Gradient background
  const gradient = ctx.createLinearGradient(0, 0, 96, 96);
  gradient.addColorStop(0, '#e50914');
  gradient.addColorStop(1, '#ff3344');
  
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, 96, 96);
  
  // Text
  ctx.fillStyle = '#ffffff';
  ctx.font = 'bold 48px Arial';
  ctx.textAlign = 'center';
  ctx.textBaseline = 'middle';
  ctx.fillText(firstChar, 48, 48);
  
  return canvas.toDataURL();
};

/**
 * React hook for token logo
 */
export const useTokenLogo = (tokenAddress, symbol) => {
  const [logo, setLogo] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;

    const fetchLogo = async () => {
      setLoading(true);
      const fetchedLogo = await getTokenLogo(tokenAddress);
      
      if (mounted) {
        setLogo(fetchedLogo || generatePlaceholderLogo(symbol));
        setLoading(false);
      }
    };

    if (tokenAddress) {
      fetchLogo();
    }

    return () => {
      mounted = false;
    };
  }, [tokenAddress, symbol]);

  return { logo, loading };
};

export default getTokenLogo;
